import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Button,
  Form,
  InputGroup,
  ListGroup,
  Badge,
  Spinner,
  Alert,
  Modal,
  Table,
  Row,
  Col,
  Pagination,
} from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { SUPPORTED_LANGUAGES } from '../../i18n/translations';
import api from '../../services/api';
import { shouldShowSupportChatSubject } from './supportChatSubject';

const SupportChat = ({
  chatId,
  onClose,
  isAdmin = false,
  onMessagesRead,
  onUnreadTotalsRefresh,
  showBackButton = false,
  /** Called when GET /support/chats/:id/ returns 404 (stale id after data loss); parent should reload list / reselect. */
  onChatMissing,
  /**
   * Non-admin: row from GET /support/chats/ (detail shape, includes messages). When set for this chatId,
   * initial load skips a duplicate GET /support/chats/<id>/.
   */
  prefetchedChatDetail = null,
}) => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageRefs = useRef(new Map());
  const highlightTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const onMessagesReadRef = useRef(onMessagesRead);
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const isMountedRef = useRef(true);
  const consumedListPayloadRef = useRef(false);
  const prefetchedDetailRef = useRef(prefetchedChatDetail);
  prefetchedDetailRef.current = prefetchedChatDetail;
  
  // Translation state
  const [translatingMessageId, setTranslatingMessageId] = useState(null);
  const [markingUnreadId, setMarkingUnreadId] = useState(null);
  const [translatingText, setTranslatingText] = useState(false);
  const [translatedText, setTranslatedText] = useState('');
  const [showTranslationPreview, setShowTranslationPreview] = useState(false);
  const [selectedTranslationLanguage, setSelectedTranslationLanguage] = useState('English');
  
  // Language mapping for GPT API
  const languageMap = {
    'en': 'English',
    'ru': 'Russian',
    'zh': 'Chinese',
    'tr': 'Turkish',
    'ar': 'Arabic',
  };
  
  // Available languages for translation (excluding Russian, as it's for user messages)
  const translationLanguages = SUPPORTED_LANGUAGES.filter(lang => lang.code !== 'ru').map(lang => ({
    code: lang.code,
    name: lang.name,
    flag: lang.flag,
    gptName: languageMap[lang.code],
  }));
  
  // Reply to message (admin only)
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  /** Admin: block under header (email, stores, …) — toggle to free vertical space while chatting. */
  const [adminUserInfoVisible, setAdminUserInfoVisible] = useState(true);
  /** Admin: AI reply draft (question pasted → suggest → insert into composer; never auto-sends). */
  const [aiQuestionDraft, setAiQuestionDraft] = useState('');
  const [aiSuggestedReply, setAiSuggestedReply] = useState('');
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  const [aiSuggestError, setAiSuggestError] = useState('');

  // Orders modal state
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersError, setOrdersError] = useState('');
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalPages, setOrdersTotalPages] = useState(0);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersSearch, setOrdersSearch] = useState('');
  const [ordersStatusFilter, setOrdersStatusFilter] = useState('');
  const [statusChoices, setStatusChoices] = useState([]);
  const [expandedOrderRow, setExpandedOrderRow] = useState(null);
  
  // Keep the ref updated
  useEffect(() => {
    onMessagesReadRef.current = onMessagesRead;
  }, [onMessagesRead]);

  useEffect(() => {
    setAdminUserInfoVisible(true);
  }, [chatId]);

  useEffect(() => {
    setAiQuestionDraft('');
    setAiSuggestedReply('');
    setAiSuggestError('');
    setAiSuggestLoading(false);
  }, [chatId]);

  // Get WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const token = api.getToken();
    if (!token) return null;
    return `${protocol}//${host}/ws/support/chat/${chatId}/?token=${encodeURIComponent(token)}`;
  }, [chatId]);

  // Connect to WebSocket
  useEffect(() => {
    isMountedRef.current = true;
    let initTimeout = null;
    
    if (!chatId) return;
    
    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      return;
    }

    // Clear any pending reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close(1000, 'Reconnecting');
      wsRef.current = null;
    }

    const createWebSocket = (url) => {
      if (!isMountedRef.current) return;
      
      // Prevent creating duplicate WebSocket for same chat
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        return;
      }

      let websocket;
      try {
        websocket = new WebSocket(url);
      } catch (err) {
        console.error('Failed to create WebSocket:', err);
        return;
      }

      websocket.onopen = () => {
        if (!isMountedRef.current) {
          websocket.close(1000, 'Component unmounted');
          return;
        }
        console.log('WebSocket connected successfully');
        setConnected(true);
        setError('');
        reconnectAttemptRef.current = 0;
        if (onMessagesReadRef.current) {
          onMessagesReadRef.current();
        }
      };

      websocket.onmessage = (event) => {
        if (!isMountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'chat_history') {
            setMessages(data.data || []);
            // Mark messages as read when history is loaded
            if (onMessagesReadRef.current) {
              onMessagesReadRef.current();
            }
          } else if (data.type === 'chat_message') {
            setMessages(prev => {
              const exists = prev.some(m => m.id === data.data.id);
              if (exists) {
                // Update existing message (e.g., when translation is added)
                return prev.map(m => m.id === data.data.id ? { ...m, ...data.data } : m);
              }
              const newMessages = [...prev, data.data];
              // If message is from other user, mark as read immediately since user is viewing the chat
              if (data.data.sender_id !== user?.id) {
                // User is actively viewing this chat, so mark as read immediately
                // WebSocket consumer will mark it as read, but we should update count right away
                if (onMessagesReadRef.current) {
                  onMessagesReadRef.current();
                }
              }
              return newMessages;
            });
          } else if (data.type === 'error') {
            setError(data.message);
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      websocket.onerror = (err) => {
        console.error('WebSocket error:', err);
      };

      websocket.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        
        if (!isMountedRef.current) return;
        
        setConnected(false);
        
        // Reconnect on abnormal closure
        if (event.code !== 1000 && event.code !== 1001) {
          const delay = Math.min(3000 * Math.pow(2, reconnectAttemptRef.current), 30000);
          console.log(`WebSocket closed unexpectedly, reconnecting in ${delay}ms (attempt ${reconnectAttemptRef.current + 1})`);
          reconnectAttemptRef.current += 1;
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              const newUrl = getWebSocketUrl();
              if (newUrl) createWebSocket(newUrl);
            }
          }, delay);
        }
      };

      wsRef.current = websocket;
    };

    const initWebSocket = () => {
      if (!isMountedRef.current) return;
      // Use requestAnimationFrame to ensure DOM is ready (fixes Firefox)
      requestAnimationFrame(() => {
        if (isMountedRef.current) {
          createWebSocket(wsUrl);
        }
      });
    };

    // Wait for page to fully load before creating WebSocket (fixes Firefox issue)
    if (document.readyState === 'complete') {
      initWebSocket();
    } else {
      const onLoad = () => {
        window.removeEventListener('load', onLoad);
        initWebSocket();
      };
      window.addEventListener('load', onLoad);
    }

    return () => {
      isMountedRef.current = false;
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, getWebSocketUrl]);

  useEffect(() => {
    consumedListPayloadRef.current = false;
  }, [chatId]);

  // Load chat: non-admin list API includes messages; defer one frame so parent `chats` ref is current (avoids duplicate GET).
  useEffect(() => {
    if (!chatId) return;

    const tryApplyPrefetch = () => {
      const p = prefetchedDetailRef.current;
      if (
        !isAdmin &&
        !consumedListPayloadRef.current &&
        p &&
        p.id === chatId &&
        Array.isArray(p.messages)
      ) {
        consumedListPayloadRef.current = true;
        setChat(p);
        setMessages(p.messages);
        setLoading(false);
        return true;
      }
      return false;
    };

    if (tryApplyPrefetch()) {
      return undefined;
    }

    const raf = requestAnimationFrame(() => {
      if (!isMountedRef.current) return;
      if (tryApplyPrefetch()) return;
      loadChat();
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, isAdmin]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const setMessageRef = useCallback((messageId, node) => {
    if (!messageId) return;
    if (node) {
      messageRefs.current.set(messageId, node);
    } else {
      messageRefs.current.delete(messageId);
    }
  }, []);

  const jumpToMessage = useCallback((messageId) => {
    if (!messageId) return;
    const targetNode = messageRefs.current.get(messageId);
    if (!targetNode) {
      setError('Исходное сообщение не найдено в текущем списке.');
      return;
    }
    targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedMessageId(messageId);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedMessageId(null);
    }, 1800);
  }, []);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedFile) || !connected) return;

    // If there's a file, upload it first
    if (selectedFile) {
      handleUploadFile();
      return;
    }

    // For admin, send via API to include translated_content if available
    if (isAdmin && translatedText && showTranslationPreview) {
      try {
        await api.sendSupportMessage(chatId, newMessage.trim(), translatedText, replyToMessage?.id);
        setNewMessage('');
        setTranslatedText('');
        setShowTranslationPreview(false);
        setReplyToMessage(null);
        // Reload chat to get updated messages
        await loadChat();
      } catch (err) {
        setError(err.data?.errorText || err.message || 'Ошибка отправки сообщения');
      }
      return;
    }

    // Send text message via WebSocket (for regular users or admin without translation)
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const payload = { type: 'chat_message', content: newMessage.trim() };
      if (replyToMessage?.id) payload.reply_to_id = replyToMessage.id;
      wsRef.current.send(JSON.stringify(payload));
      setNewMessage('');
      setTranslatedText('');
      setShowTranslationPreview(false);
      setReplyToMessage(null);
    }
  };

  // Handle file upload
  const handleUploadFile = async () => {
    if (!selectedFile || !chatId || uploadingFile) return;

    try {
      setUploadingFile(true);
      const translatedContent = (isAdmin && translatedText && showTranslationPreview) ? translatedText : undefined;
      const response = await api.uploadSupportMessageAttachment(
        chatId,
        selectedFile,
        newMessage.trim() || undefined,
        translatedContent,
        replyToMessage?.id || null
      );
      
        if (response.data) {
        // File uploaded successfully, add message to chat
        setMessages(prev => {
          // Check if message already exists (avoid duplicates)
          const exists = prev.some(m => m.id === response.data.id);
          if (exists) return prev;
          return [...prev, response.data];
        });
        setSelectedFile(null);
        setNewMessage('');
        setTranslatedText('');
        setShowTranslationPreview(false);
        setReplyToMessage(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (err) {
      setError(err.data?.errorText || err.message || t('support.failedToUploadFile'));
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(t('support.fileSizeExceeded', { 
        currentSize: (file.size / (1024 * 1024)).toFixed(2) 
      }));
      return;
    }

    setSelectedFile(file);
    setError('');
  };

  // Load chat details and messages from API (fast initial load)
  const loadChat = async () => {
    if (!chatId) return;
    
    try {
      const response = await api.getSupportChat(chatId);
      if (response.data && isMountedRef.current) {
        setChat(response.data);
        // Load messages from API immediately for fast display
        setMessages(response.data.messages || []);
        setLoading(false);
        // WebSocket will update messages in real-time when connected
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err.data?.errorText || t('support.failedToLoadChat'));
        setLoading(false);
        if (err?.status === 404) {
          onChatMissing?.();
        }
      }
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    setShowTranslationPreview(false);
    setTranslatedText('');
  };

  // Translate user message (admin only)
  const handleTranslateMessage = async (messageId) => {
    if (!isAdmin) return;
    
    try {
      setTranslatingMessageId(messageId);
      const response = await api.translateMessage(messageId, 'Russian');
      if (response.data) {
        // Update message in local state
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, translated_content: response.data.translated_content }
            : msg
        ));
      }
    } catch (err) {
      setError(err.data?.errorText || err.message || 'Ошибка перевода');
    } finally {
      setTranslatingMessageId(null);
    }
  };

  const handleMarkUnread = async (messageId) => {
    if (!isAdmin) return;
    try {
      setMarkingUnreadId(messageId);
      await api.markSupportMessageUnread(messageId);
      setMessages(prev =>
        prev.map(msg => (msg.id === messageId ? { ...msg, is_read: false } : msg))
      );
      onUnreadTotalsRefresh?.();
    } catch (err) {
      setError(err.data?.errorText || err.message || t('support.markUnreadFailed'));
    } finally {
      setMarkingUnreadId(null);
    }
  };

  // Translate admin message before sending
  const handleTranslateText = async (text, targetLanguage = null) => {
    if (!text.trim() || !isAdmin) return;
    
    const languageToUse = targetLanguage || selectedTranslationLanguage;
    
    try {
      setTranslatingText(true);
      const response = await api.translateText(text, languageToUse);
      if (response.data && response.data.translated_text) {
        setTranslatedText(response.data.translated_text);
        setShowTranslationPreview(true);
      }
    } catch (err) {
      setError(err.data?.errorText || err.message || 'Ошибка перевода');
    } finally {
      setTranslatingText(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return t('support.justNow');
    if (minutes < 60) return t('support.minutesAgo', { minutes });
    // Use locale based on current language
    const localeMap = { en: 'en-US', ru: 'ru-RU', zh: 'zh-CN', tr: 'tr-TR' };
    const locale = localeMap[language] || 'en-US';
    if (diff < 86400000) return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString(locale) + ' ' + date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  };

  // Load status choices for orders
  useEffect(() => {
    if (isAdmin) {
      const loadStatusChoices = async () => {
        try {
          const response = await api.request('/orders/status-choices/', { method: 'GET' });
          if (response.data) {
            setStatusChoices(response.data);
          }
        } catch (err) {
          console.error('Failed to load status choices:', err);
        }
      };
      loadStatusChoices();
    }
  }, [isAdmin]);

  // Load user orders for modal
  const loadUserOrders = useCallback(async () => {
    if (!chat?.user) return;
    
    setOrdersLoading(true);
    setOrdersError('');
    
    try {
      const params = {
        user_id: chat.user,
        page: ordersPage,
        page_size: 20,
      };
      
      if (ordersSearch) params.search = ordersSearch;
      if (ordersStatusFilter) params.status = ordersStatusFilter;
      
      const response = await api.getAdminOrders(params);
      if (response.data) {
        setOrders(response.data);
        setOrdersTotal(response.total || 0);
        setOrdersTotalPages(response.total_pages || 0);
      }
    } catch (err) {
      setOrdersError(err.data?.errorText || err.message || 'Ошибка загрузки заказов');
    } finally {
      setOrdersLoading(false);
    }
  }, [chat?.user, ordersPage, ordersSearch, ordersStatusFilter]);

  // Load orders when modal opens
  useEffect(() => {
    if (showOrdersModal && chat?.user) {
      loadUserOrders();
    }
  }, [showOrdersModal, loadUserOrders, chat?.user]);

  // Handle orders search
  const handleOrdersSearch = () => {
    setOrdersPage(1);
    loadUserOrders();
  };

  // Get status label
  const getStatusLabel = (statusValue) => {
    const statusChoice = statusChoices.find(s => s.value === statusValue);
    return statusChoice ? statusChoice.display : statusValue;
  };

  // Open Django Admin with user filter
  const openDjangoAdminOrders = () => {
    if (chat?.user) {
      window.open(`/admin/orders/order/?items__order__seller_id__in=${chat.user_info?.stores?.map(s => s.store_id).join(',') || ''}`, '_blank');
    }
  };

  // Show spinner only while loading
  if (loading) {
    return (
      <Card className="h-100">
        <Card.Body className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  if (!chat) {
    return (
      <Card className="h-100">
        <Card.Body>
          <Alert variant="danger">{t('support.chatNotFound')}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100 d-flex flex-column" style={{ minHeight: 0 }}>
      <Card.Header className="flex-shrink-0">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            {showBackButton && onClose && (
              <Button variant="outline-secondary" size="sm" className="me-2" onClick={onClose}>
                <i className="bi bi-arrow-left"></i>
              </Button>
            )}
            <div>
              <h5 className="mb-0" style={{ color: 'var(--bs-body-color)', fontSize: showBackButton ? '1rem' : undefined }}>
                {isAdmin ? t('support.chatWith', { name: chat.user_name || chat.user_email }) : t('support.supportChat')}
                {shouldShowSupportChatSubject(chat.subject) && (
                  <small className="ms-2 d-none d-sm-inline" style={{ color: 'var(--bs-body-color)', fontWeight: 'normal' }}>
                    - {chat.subject}
                    {isAdmin && chat.subject_translated && chat.subject && chat.subject !== chat.subject_translated && (
                      <span className="ms-2" style={{ fontStyle: 'italic', opacity: 0.8 }}>
                        <i className="bi bi-translate me-1"></i>
                        {chat.subject_translated}
                      </span>
                    )}
                  </small>
                )}
              </h5>
              {shouldShowSupportChatSubject(chat.subject) && showBackButton && (
                <small className="d-block d-sm-none" style={{ color: 'var(--bs-body-color)' }}>
                  {chat.subject}
                  {isAdmin && chat.subject_translated && chat.subject && chat.subject !== chat.subject_translated && (
                    <span className="ms-2 d-block mt-1" style={{ fontStyle: 'italic', opacity: 0.8 }}>
                      <i className="bi bi-translate me-1"></i>
                      {chat.subject_translated}
                    </span>
                  )}
                </small>
              )}
              <div className="mt-1">
                {connected && (
                  <Badge bg="success">{t('support.online')}</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {isAdmin && chat.user && (
              <>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowOrdersModal(true)}
                  title="Заказы пользователя"
                >
                  <i className="bi bi-box-seam me-1"></i>
                  <span className="d-none d-lg-inline">Заказы</span>
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => window.open(`/admin/orders?user_id=${chat.user}`, '_blank')}
                  title="Открыть в админ-панели"
                >
                  <i className="bi bi-box-arrow-up-right me-1"></i>
                  <span className="d-none d-lg-inline">Админ</span>
                </Button>
                <Button
                  variant={adminUserInfoVisible ? 'outline-info' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setAdminUserInfoVisible((v) => !v)}
                  title={adminUserInfoVisible ? 'Скрыть данные пользователя' : 'Показать данные пользователя'}
                >
                  <i className="bi bi-info-circle me-1"></i>
                  <span className="d-none d-lg-inline">Инфо</span>
                </Button>
              </>
            )}
            {onClose && !showBackButton && (
              <Button variant="outline-secondary" size="sm" onClick={onClose}>
                <i className="bi bi-x"></i>
              </Button>
            )}
          </div>
        </div>
        
        {/* User Info Panel */}
        {isAdmin && chat.user_info && adminUserInfoVisible && (
            <div className="mt-3 p-3 rounded support-user-info-panel" style={{ 
              backgroundColor: 'var(--bg-tertiary)', 
              color: 'var(--text-primary)'
            }}>
              <Row>
                <Col xs={12} md={6}>
                  <h6 className="mb-2 support-user-info-text" style={{ color: 'var(--text-primary) !important' }}>
                    <i className="bi bi-person-circle me-2"></i>Пользователь
                  </h6>
                  <div className="small support-user-info-text">
                    <p className="mb-1 support-user-info-text"><strong className="support-user-info-text">Email:</strong> <span className="support-user-info-text">{chat.user_info.email}</span></p>
                    <p className="mb-1 support-user-info-text"><strong className="support-user-info-text">Имя:</strong> <span className="support-user-info-text">{chat.user_info.full_name}</span></p>
                    {chat.user_info.delivery_address_phone && (
                      <p className="mb-1 support-user-info-text"><strong className="support-user-info-text">Телефон:</strong> <span className="support-user-info-text">{chat.user_info.delivery_address_phone}</span></p>
                    )}
                    {(chat.user_info.delivery_address_city || chat.user_info.delivery_address_country) && (
                      <p className="mb-1 support-user-info-text">
                        <strong className="support-user-info-text">Локация:</strong> <span className="support-user-info-text">{[chat.user_info.delivery_address_city, chat.user_info.delivery_address_country].filter(Boolean).join(', ')}</span>
                      </p>
                    )}
                    {chat.user_info.delivery_address_street && (
                      <p className="mb-1 support-user-info-text">
                        <strong className="support-user-info-text">Адрес:</strong>{' '}
                        <span className="support-user-info-text">{chat.user_info.delivery_address_street}</span>
                        {chat.user_info.delivery_address_postal_code ? (
                          <span className="support-user-info-text">, {chat.user_info.delivery_address_postal_code}</span>
                        ) : null}
                      </p>
                    )}
                    {chat.user_info.wechat_id && (
                      <p className="mb-1 support-user-info-text">
                        <strong className="support-user-info-text">WeChat:</strong>{' '}
                        <span className="support-user-info-text">{chat.user_info.wechat_id}</span>
                      </p>
                    )}
                    {chat.user_info.telegram_username && (
                      <p className="mb-1 support-user-info-text">
                        <strong className="support-user-info-text">Telegram:</strong>{' '}
                        <span className="support-user-info-text">
                          {String(chat.user_info.telegram_username).startsWith('@')
                            ? chat.user_info.telegram_username
                            : `@${chat.user_info.telegram_username}`}
                        </span>
                      </p>
                    )}
                    {chat.user_info.balance_top_up != null && chat.user_info.balance_top_up !== '' && (
                      <p className="mb-1 support-user-info-text">
                        <strong className="support-user-info-text">Пополнение баланса (учёт):</strong>{' '}
                        <span className="support-user-info-text">{chat.user_info.balance_top_up} ¥</span>
                      </p>
                    )}
                    <p className="mb-1 support-user-info-text">
                      <strong className="support-user-info-text">Статус:</strong>{' '}
                      {chat.user_info.is_active ? (
                        <Badge bg="success" className="ms-1">Активен</Badge>
                      ) : (
                        <Badge bg="danger" className="ms-1">Неактивен</Badge>
                      )}
                      {chat.user_info.is_email_verified && (
                        <Badge bg="info" className="ms-1">Email подтверждён</Badge>
                      )}
                    </p>
                    <p className="mb-0 support-user-info-text"><strong className="support-user-info-text">Регистрация:</strong> <span className="support-user-info-text">{new Date(chat.user_info.date_joined).toLocaleDateString()}</span></p>
                  </div>
                </Col>
                <Col xs={12} md={6} className="mt-3 mt-md-0">
                  <h6 className="mb-2 support-user-info-text" style={{ color: 'var(--text-primary) !important' }}>
                    <i className="bi bi-shop me-2"></i>
                    Магазины ({chat.user_info.verified_stores_count}/{chat.user_info.stores_count})
                  </h6>
                  {chat.user_info.stores && chat.user_info.stores.length > 0 ? (
                    <div className="small" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {chat.user_info.stores.map((store, idx) => (
                        <div key={store.id} className="mb-2 p-2 rounded support-user-info-text" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="support-user-info-text" style={{ color: 'var(--text-primary) !important' }}>
                              <strong className="support-user-info-text">{store.store_type_display}</strong>
                              {store.name && <span className="ms-1 support-user-info-text">({store.name})</span>}
                            </span>
                            <Badge bg={store.verification_status === 'verified' ? 'success' : store.verification_status === 'pending' ? 'warning' : 'danger'}>
                              {store.verification_status_display}
                            </Badge>
                          </div>
                          <small className="support-user-info-text" style={{ color: 'var(--text-primary) !important' }}>ID: {store.store_id}</small>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="small mb-0 support-user-info-text" style={{ color: 'var(--text-primary) !important' }}>Нет магазинов</p>
                  )}
                </Col>
              </Row>
            </div>
        )}
      </Card.Header>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="m-2 mb-0">
          {error}
        </Alert>
      )}

      <Card.Body className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
        <ListGroup variant="flush">
          {messages.map((message) => {
            const isOwnMessage = message.sender_id === user?.id;
            return (
              <ListGroup.Item
                key={message.id}
                className={`d-flex ${isOwnMessage ? 'justify-content-end' : 'justify-content-start'} border-0 px-2 py-1`}
                ref={(node) => setMessageRef(message.id, node)}
              >
                <div
                  className={`p-2 rounded ${
                    isOwnMessage
                      ? 'bg-primary text-white'
                      : 'bg-secondary text-white'
                  }`}
                  style={{ 
                    maxWidth: '70%',
                    backgroundColor: isOwnMessage ? undefined : 'var(--bs-secondary)',
                    color: isOwnMessage ? undefined : 'white',
                    boxShadow: highlightedMessageId === message.id ? '0 0 0 2px rgba(255, 193, 7, 0.8)' : undefined,
                    transition: 'box-shadow 0.25s ease'
                  }}
                >
                  {!isOwnMessage && (
                    <div className="small fw-bold mb-1 d-flex justify-content-between align-items-center gap-2" style={{ color: 'white' }}>
                      <span>{isAdmin ? (message.sender_name || message.sender_email) : t('support.support')}</span>
                      <Button
                        variant="outline-light"
                        size="sm"
                        className="py-0 px-1"
                        title={t('support.replyToMessage')}
                        onClick={() => setReplyToMessage({
                          id: message.id,
                          content: (message.content || '').slice(0, 150),
                          sender_name: isAdmin ? (message.sender_name || message.sender_email) : t('support.support'),
                        })}
                      >
                        <i className="bi bi-reply me-1"></i>
                        {t('support.reply')}
                      </Button>
                    </div>
                  )}
                  {message.reply_to && (
                    <div
                      className="mb-2 p-2 rounded small"
                      style={{
                        borderLeft: '3px solid rgba(255,255,255,0.5)',
                        backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.2)',
                        color: isOwnMessage ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.9)',
                        cursor: 'pointer',
                      }}
                      onClick={() => jumpToMessage(message.reply_to.id)}
                      title="Перейти к исходному сообщению"
                    >
                      <div className="fw-bold" style={{ fontSize: '0.8em' }}>
                        {message.reply_to.sender_name}
                      </div>
                      <div style={{ fontSize: '0.85em', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {(message.reply_to.content || '').slice(0, 200)}
                        {(message.reply_to.content || '').length > 200 ? '…' : ''}
                      </div>
                    </div>
                  )}
                  {message.content && (
                    <div className="mb-2" style={{ color: isOwnMessage ? undefined : 'white' }}>
                      <div>{message.content}</div>
                      {/* Show translated content for user messages (admin only) - рядом с оригиналом */}
                      {!isOwnMessage && isAdmin && message.translated_content && (
                        <div className="mt-1" style={{ fontSize: '0.9em', opacity: 0.9, fontStyle: 'italic' }}>
                          <i className="bi bi-translate me-1"></i>
                          <span>{message.translated_content}</span>
                        </div>
                      )}
                      {/* Translate button for user messages (admin only) */}
                      {!isOwnMessage && isAdmin && !message.translated_content && message.content && (
                        <div className="mt-2">
                          <Button
                            variant="outline-light"
                            size="sm"
                            onClick={() => handleTranslateMessage(message.id)}
                            disabled={translatingMessageId === message.id}
                            style={{ fontSize: '0.75rem' }}
                          >
                            {translatingMessageId === message.id ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Перевод...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-translate me-1"></i>
                                Перевести на русский
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                      {isAdmin && message.message_type === 'user' && (
                        <div className="mt-2">
                          <Button
                            variant="outline-light"
                            size="sm"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => handleMarkUnread(message.id)}
                            disabled={markingUnreadId === message.id}
                          >
                            {markingUnreadId === message.id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <>
                                <i className="bi bi-envelope-open me-1"></i>
                                {t('support.markAsUnread')}
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  {message.file_url && (
                    <div className="mb-2">
                      {message.attachment_type === 'photo' ? (
                        <img
                          src={api.getAuthenticatedMediaUrl(message.file_url)}
                          alt={message.file_name || 'Photo'}
                          style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px', cursor: 'pointer' }}
                          onClick={() => window.open(api.getAuthenticatedMediaUrl(message.file_url), '_blank')}
                        />
                      ) : message.attachment_type === 'video' ? (
                        <video
                          src={api.getAuthenticatedMediaUrl(message.file_url)}
                          controls
                          style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px' }}
                        />
                      ) : (
                        <div className="p-3 border rounded" style={{ backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                          <i className="bi bi-file-earmark me-2"></i>
                          <a
                            href={api.getAuthenticatedMediaUrl(message.file_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: isOwnMessage ? '#fff' : '#0d6efd', textDecoration: 'none' }}
                          >
                            {message.file_name || 'Download file'}
                          </a>
                        </div>
                      )}
                      {message.file_name && (
                        <div className="small mt-1" style={{ color: isOwnMessage ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.7)' }}>
                          <i className="bi bi-paperclip me-1"></i>
                          {message.file_name}
                          {message.file_size && (
                            <span className="ms-1">
                              ({(message.file_size / (1024 * 1024)).toFixed(2)} MB)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="small mt-1 d-flex justify-content-between align-items-center" style={{ color: isOwnMessage ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.7)' }}>
                    <span>{formatDate(message.created_at)}</span>
                    {isOwnMessage && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-2 text-decoration-none"
                        style={{ color: 'inherit', opacity: 0.9, fontSize: '0.85rem' }}
                        title={t('support.replyToMessage')}
                        onClick={() => setReplyToMessage({
                          id: message.id,
                          content: (message.content || '').slice(0, 150),
                          sender_name: message.sender_name || message.sender_email || t('support.support'),
                        })}
                      >
                        <i className="bi bi-reply me-1"></i>
                        {t('support.reply')}
                      </Button>
                    )}
                  </div>
                </div>
              </ListGroup.Item>
            );
          })}
          <div ref={messagesEndRef} />
        </ListGroup>
      </Card.Body>

      <Card.Footer className="flex-shrink-0">
          <>
            {selectedFile && (
              <div className="mb-2 p-2 bg-light rounded d-flex justify-content-between align-items-center">
                <div>
                  <i className="bi bi-paperclip me-2"></i>
                  <span>{selectedFile.name}</span>
                  <small className="text-muted ms-2">
                    ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
                  </small>
                </div>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                >
                  <i className="bi bi-x"></i>
                </Button>
              </div>
            )}
            <Form onSubmit={handleSendMessage}>
              {isAdmin && (
                <div
                  className="mb-3 p-2 rounded border"
                  style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
                >
                  <div className="small fw-bold mb-1">{t('support.aiSuggestTitle')}</div>
                  <div className="small text-muted mb-2">{t('support.aiSuggestHint')}</div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    className="mb-2"
                    placeholder={t('support.enterMessage')}
                    value={aiQuestionDraft}
                    onChange={(e) => setAiQuestionDraft(e.target.value)}
                  />
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={async () => {
                        setAiSuggestError('');
                        try {
                          const text = await navigator.clipboard.readText();
                          setAiQuestionDraft(text);
                        } catch {
                          setAiSuggestError(t('support.aiSuggestError'));
                        }
                      }}
                    >
                      {t('support.aiSuggestPasteClipboard')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-primary"
                      size="sm"
                      disabled={aiSuggestLoading || !aiQuestionDraft.trim()}
                      onClick={async () => {
                        setAiSuggestError('');
                        setAiSuggestedReply('');
                        setAiSuggestLoading(true);
                        try {
                          const res = await api.postSupportAdminAiReplySuggest(aiQuestionDraft);
                          const s = res?.data?.suggested_reply;
                          if (s) setAiSuggestedReply(s);
                          else setAiSuggestError(t('support.aiSuggestError'));
                        } catch (err) {
                          setAiSuggestError(err.data?.errorText || t('support.aiSuggestError'));
                        } finally {
                          setAiSuggestLoading(false);
                        }
                      }}
                    >
                      {aiSuggestLoading ? t('support.aiSuggestLoading') : t('support.aiSuggestGetDraft')}
                    </Button>
                  </div>
                  {aiSuggestError && (
                    <Alert variant="warning" className="py-1 small mb-2">
                      {aiSuggestError}
                    </Alert>
                  )}
                  {aiSuggestedReply && (
                    <div>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        readOnly
                        className="mb-2 small"
                        value={aiSuggestedReply}
                      />
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setNewMessage(aiSuggestedReply);
                          setShowTranslationPreview(false);
                          setTranslatedText('');
                        }}
                      >
                        {t('support.aiSuggestInsert')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {/* Reply-to preview */}
              {replyToMessage && (
                <div className="mb-2 p-2 rounded d-flex justify-content-between align-items-start" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                  <div className="small flex-grow-1">
                    <span className="fw-bold">Ответ на: </span>
                    <span className="text-muted">{replyToMessage.sender_name}</span>
                    <div className="mt-1" style={{ fontSize: '0.9em', opacity: 0.9 }}>
                      {(replyToMessage.content || '').slice(0, 100)}
                      {(replyToMessage.content || '').length > 100 ? '…' : ''}
                    </div>
                  </div>
                  <Button
                    variant="link"
                    size="sm"
                    className="text-secondary p-0 ms-2"
                    onClick={() => setReplyToMessage(null)}
                    title="Отменить ответ"
                  >
                    <i className="bi bi-x-lg"></i>
                  </Button>
                </div>
              )}
              {/* Translation preview for admin */}
              {isAdmin && showTranslationPreview && translatedText && (
                <div className="mb-2 p-2 rounded border support-translation-preview" style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                  <div className="small mb-1 fw-bold support-translation-preview-text" style={{ color: 'var(--text-primary) !important' }}>
                    <i className="bi bi-translate me-1"></i>
                    Перевод на {translationLanguages.find(l => l.gptName === selectedTranslationLanguage)?.name || selectedTranslationLanguage}:
                  </div>
                  <div className="mb-2 support-translation-preview-text" style={{ color: 'var(--text-primary) !important' }}>{translatedText}</div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        setNewMessage(translatedText);
                        setShowTranslationPreview(false);
                        setTranslatedText('');
                        // Keep the selected language for next translation
                      }}
                    >
                      Использовать перевод
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setShowTranslationPreview(false);
                        setTranslatedText('');
                      }}
                    >
                      Отмена
                    </Button>
                  </div>
                </div>
              )}
              {/* Language selection for translation (admin only) */}
              {isAdmin && newMessage.trim() && (
                <div className="mb-2 d-flex align-items-center gap-2">
                  <span className="small support-translation-label" style={{ color: 'var(--text-primary)' }}>
                    <i className="bi bi-translate me-1"></i>Перевести на:
                  </span>
                  <div className="btn-group btn-group-sm" role="group">
                    {translationLanguages.map((lang) => (
                      <Button
                        key={lang.code}
                        variant={selectedTranslationLanguage === lang.gptName ? 'primary' : 'outline-secondary'}
                        size="sm"
                        onClick={() => setSelectedTranslationLanguage(lang.gptName)}
                        title={lang.name}
                      >
                        <span className="me-1">{lang.flag}</span>
                        <span className="d-none d-sm-inline">{lang.name}</span>
                      </Button>
                    ))}
                  </div>
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => handleTranslateText(newMessage)}
                    disabled={!connected || uploadingFile || translatingText}
                    title={`Перевести на ${translationLanguages.find(l => l.gptName === selectedTranslationLanguage)?.name || selectedTranslationLanguage}`}
                  >
                    {translatingText ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        Перевод...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-translate me-1"></i>
                        <span className="d-none d-sm-inline">Перевести</span>
                      </>
                    )}
                  </Button>
                </div>
              )}
              <InputGroup>
                <Button
                  variant="outline-secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!connected || uploadingFile}
                >
                  <i className="bi bi-paperclip"></i>
                </Button>
                <Form.Control
                  type="text"
                  placeholder={t('support.enterMessage')}
                  value={newMessage}
                  onChange={handleInputChange}
                  disabled={!connected || uploadingFile}
                />
                <Button
                  variant="primary"
                  type="submit"
                  disabled={(!newMessage.trim() && !selectedFile) || !connected || uploadingFile}
                >
                  {uploadingFile ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    t('support.send')
                  )}
                </Button>
              </InputGroup>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </Form>
          </>
      </Card.Footer>

      {/* Orders Modal */}
      <Modal 
        show={showOrdersModal} 
        onHide={() => setShowOrdersModal(false)} 
        size="xl"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: 'var(--bs-body-color)' }}>
            <i className="bi bi-box-seam me-2"></i>
            Заказы пользователя
            {chat?.user_info && (
              <small className="ms-2" style={{ color: 'var(--bs-secondary-color)' }}>
                ({chat.user_info.full_name})
              </small>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {ordersError && (
            <Alert variant="danger" dismissible onClose={() => setOrdersError('')}>
              {ordersError}
            </Alert>
          )}

          {/* Filters */}
          <Row className="mb-3 g-2">
            <Col xs={12} sm={6} md={4}>
              <InputGroup size="sm">
                <Form.Control
                  type="text"
                  placeholder="Поиск по номеру, SKU..."
                  value={ordersSearch}
                  onChange={(e) => setOrdersSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleOrdersSearch()}
                />
                <Button variant="outline-secondary" onClick={handleOrdersSearch}>
                  <i className="bi bi-search"></i>
                </Button>
              </InputGroup>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Form.Select 
                size="sm" 
                value={ordersStatusFilter} 
                onChange={(e) => { setOrdersStatusFilter(e.target.value); setOrdersPage(1); }}
              >
                <option value="">Все статусы</option>
                {statusChoices.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.display}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={5} className="text-md-end">
              <small style={{ color: 'var(--bs-secondary-color)' }}>
                Найдено заказов: {ordersTotal}
              </small>
            </Col>
          </Row>

          {/* Orders Table */}
          {ordersLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-5" style={{ color: 'var(--bs-secondary-color)' }}>
              <i className="bi bi-inbox display-4 d-block mb-2"></i>
              Заказы не найдены
            </div>
          ) : (
            <>
              <div className="table-wrapper">
                <Table hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ color: 'var(--bs-body-color)' }}>№ заказа</th>
                      <th style={{ color: 'var(--bs-body-color)' }}>Статус</th>
                      <th style={{ color: 'var(--bs-body-color)' }}>Магазин</th>
                      <th className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)' }}>Товар</th>
                      <th className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)' }}>Дата</th>
                      <th style={{ color: 'var(--bs-body-color)' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => {
                      const item = order.items?.[0];
                      const isExpanded = expandedOrderRow === order.id;
                      return (
                        <React.Fragment key={order.id}>
                          <tr>
                            <td style={{ color: 'var(--bs-body-color)' }}>
                              <small>{order.platform_order_no}</small>
                            </td>
                            <td>
                              <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                                {getStatusLabel(order.status)}
                              </Badge>
                            </td>
                            <td style={{ color: 'var(--bs-body-color)' }}>
                              <small>{order.seller_id || '-'}</small>
                            </td>
                            <td className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)' }}>
                              {item && (
                                <small>
                                  {item.sku_name ? (
                                    item.sku_name.length > 30 ? item.sku_name.substring(0, 30) + '...' : item.sku_name
                                  ) : item.sku_code}
                                </small>
                              )}
                            </td>
                            <td className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)' }}>
                              <small>{new Date(order.created_at).toLocaleDateString()}</small>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                onClick={() => setExpandedOrderRow(isExpanded ? null : order.id)}
                              >
                                <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                              </Button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan="6" style={{ backgroundColor: 'var(--bs-tertiary-bg)' }}>
                                <div className="p-2">
                                  <Row>
                                    <Col xs={12} md={6}>
                                      <small style={{ color: 'var(--bs-body-color)' }}>
                                        {item && (
                                          <>
                                            <p className="mb-1"><strong>Товар:</strong> {item.sku_name || item.sku_code}</p>
                                            <p className="mb-1"><strong>SKU:</strong> {item.sku_code}</p>
                                            {item.sku_barcode && <p className="mb-1"><strong>Штрихкод:</strong> {item.sku_barcode}</p>}
                                            <p className="mb-1 text-muted small">
                                              <strong>Габариты / вес:</strong>{' '}
                                              {(() => {
                                                const L = item.sku_length;
                                                const W = item.sku_width;
                                                const H = item.sku_height;
                                                const hasDims =
                                                  (L != null && Number(L) > 0) ||
                                                  (W != null && Number(W) > 0) ||
                                                  (H != null && Number(H) > 0);
                                                const wkg =
                                                  item.sku_weight_kg != null && item.sku_weight_kg !== ''
                                                    ? Number(item.sku_weight_kg)
                                                    : NaN;
                                                const hasW = !Number.isNaN(wkg) && wkg > 0;
                                                if (!hasDims && !hasW) return 'не указаны';
                                                const d = hasDims ? `${L || 0}×${W || 0}×${H || 0} см` : '—';
                                                const w = hasW ? `${wkg} кг` : 'вес неизвестен';
                                                return `${d} · ${w}`;
                                              })()}
                                            </p>
                                            <p className="mb-1"><strong>Брак:</strong> {item.is_defective !== null ? (item.is_defective ? 'Да' : 'Нет') : '-'}</p>
                                          </>
                                        )}
                                      </small>
                                    </Col>
                                    <Col xs={12} md={6}>
                                      <small style={{ color: 'var(--bs-body-color)' }}>
                                        {order.tracking_number && <p className="mb-1"><strong>Трек:</strong> {order.tracking_number}</p>}
                                        {order.customer_tracking_number && <p className="mb-1"><strong>Трек клиента:</strong> {order.customer_tracking_number}</p>}
                                        {order.seller_tracking_number && <p className="mb-1"><strong>Трек продавца:</strong> {order.seller_tracking_number}</p>}
                                        <p className="mb-1"><strong>Создан:</strong> {new Date(order.created_at).toLocaleString()}</p>
                                      </small>
                                      {item?.attachments?.length > 0 && (
                                        <div className="mt-2">
                                          <strong className="small">Вложения:</strong>
                                          <div className="d-flex flex-wrap gap-1 mt-1">
                                            {item.attachments.map((att) => (
                                              <img
                                                key={att.id}
                                                src={api.getAuthenticatedMediaUrl(att.file_url)}
                                                alt={att.file_name}
                                                style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                                                onClick={() => window.open(api.getAuthenticatedMediaUrl(att.file_url), '_blank')}
                                              />
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </Col>
                                  </Row>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {ordersTotalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination size="sm">
                    <Pagination.First onClick={() => setOrdersPage(1)} disabled={ordersPage === 1} />
                    <Pagination.Prev onClick={() => setOrdersPage(p => Math.max(1, p - 1))} disabled={ordersPage === 1} />
                    {[...Array(Math.min(5, ordersTotalPages))].map((_, i) => {
                      let page;
                      if (ordersTotalPages <= 5) {
                        page = i + 1;
                      } else if (ordersPage <= 3) {
                        page = i + 1;
                      } else if (ordersPage >= ordersTotalPages - 2) {
                        page = ordersTotalPages - 4 + i;
                      } else {
                        page = ordersPage - 2 + i;
                      }
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === ordersPage}
                          onClick={() => setOrdersPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next onClick={() => setOrdersPage(p => Math.min(ordersTotalPages, p + 1))} disabled={ordersPage === ordersTotalPages} />
                    <Pagination.Last onClick={() => setOrdersPage(ordersTotalPages)} disabled={ordersPage === ordersTotalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => window.open(`/admin/orders?user_id=${chat?.user}`, '_blank')}
          >
            <i className="bi bi-box-arrow-up-right me-1"></i>
            Открыть в полной версии
          </Button>
          <Button variant="secondary" onClick={() => setShowOrdersModal(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default SupportChat;

